import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { environment } from '@core/environments/environment';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { IEvent } from '@shared/interfaces/event.interface';
import { EAiConnectorTab, IAiConnectorDialogData } from './ai-connector-dialog.interface';
import { buildClaudeConnectorUrl, readAiConnectorStorage, writeAiConnectorStorage } from './ai-connector.util';

const LAST_TAB_STORAGE_KEY = 'aiConnectorLastTab';

@Component({
  imports: [
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    NgTemplateOutlet,
    SideDialogComponent,
    TranslateModule,
  ],
  selector: 'app-ai-connector-dialog',
  templateUrl: './ai-connector-dialog.component.html',
})
export class AiConnectorDialogComponent {
  public readonly EAiConnectorTab = EAiConnectorTab;
  public readonly mcpServerUrl = environment.mcpServerUrl;
  public readonly claudeConnectorUrl = buildClaudeConnectorUrl(environment.mcpServerUrl);
  public readonly chatGptEnabled = environment.aiConnectorChatGptEnabled;
  // Tab order as rendered; also validates the requested/stored tab against the ChatGPT flag.
  public readonly tabs: EAiConnectorTab[] = this.chatGptEnabled
    ? [EAiConnectorTab.claude, EAiConnectorTab.chatgpt, EAiConnectorTab.other]
    : [EAiConnectorTab.claude, EAiConnectorTab.other];
  public readonly selectedTab = signal<EAiConnectorTab>(EAiConnectorTab.claude);

  private readonly data = inject<IAiConnectorDialogData>(MAT_DIALOG_DATA);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  constructor() {
    const storedTab = readAiConnectorStorage(LAST_TAB_STORAGE_KEY) as EAiConnectorTab | null;
    const initialTab =
      [this.data.tab, storedTab].find((tab): tab is EAiConnectorTab => !!tab && this.tabs.includes(tab)) ??
      EAiConnectorTab.claude;

    this.selectedTab.set(initialTab);
    writeAiConnectorStorage(LAST_TAB_STORAGE_KEY, initialTab);
    this.track(EEventType.mcpDialogOpened, { newValue: initialTab });

    // Destroyed on every way out (close buttons, Escape, backdrop, navigation).
    const openedAt = Date.now();
    inject(DestroyRef).onDestroy(() => this.track(EEventType.mcpDialogClosed, { timeSpent: Date.now() - openedAt }));
  }

  public onTabClick(tab: EAiConnectorTab): void {
    const previousTab = this.selectedTab();

    if (tab === previousTab) {
      return;
    }

    this.selectedTab.set(tab);
    writeAiConnectorStorage(LAST_TAB_STORAGE_KEY, tab);
    this.track(EEventType.mcpDialogTabOpened, { newValue: tab, previousValue: previousTab });
  }

  public onOpenInClaudeClick(): void {
    this.track(EEventType.mcpOpenInClaudeClicked);
  }

  public async onCopyServerUrlClick(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.mcpServerUrl);
    } catch {
      this.snackbarService.openError(this.translate.instant('shared.aiConnector.copyError'));

      return;
    }

    this.snackbarService.openSuccess(this.translate.instant('shared.aiConnector.copied'));
    this.track(EEventType.mcpServerUrlCopied);
  }

  private track(eventType: EEventType, properties: Partial<IEvent> = {}): void {
    this.eventTrackingService.track(eventType, {
      mcpConnectorTab: this.selectedTab(),
      source: this.data.source,
      ...properties,
    });
  }
}
